import { NodeStatusEvent } from '@/types/workflow-types';
import { getFreshToken } from '@/utils/auth-token-provider';
import { convertPythonDictToJSON } from '@/utils/convert-python-dict';
import { normalizeServerTimestamp } from '@/utils/normalize-server-timestamp';
import * as Sentry from '@sentry/react';
import { isCapWsDebugEnvironment, registerCapWsDebug } from './cap-ws-debug';

/**
 * WebSocketService - Manages WebSocket connections for workflow execution
 *
 * DEBUGGING LOGS:
 * All logs are prefixed with [WebSocket] for easy filtering.
 * Key events logged:
 * - Token changes (setAuthToken)
 * - Connection attempts (connect)
 * - URL building (buildWebSocketUrl) - warns if no token
 * - Connection state changes (onopen, onclose, onerror)
 * - Reconnection attempts (attemptReconnect)
 * - URL updates (updateUrl)
 *
 * CRITICAL WARNINGS:
 * - "Refusing to connect: no session URL has been set" - connect before updateUrl
 * - "Reconnection stopped" with reason "No session URL set" - same cause, retry loop
 * - "CRITICAL: Attempting to connect without auth token" - connection will likely fail
 * - "Token set to null while connected" - reconnection will fail
 * - "Connection closed: POLICY VIOLATION" - auth failure (code 1008)
 * - "All reconnection attempts exhausted" - max retries reached
 *
 * Close codes:
 * - 1000: Normal closure
 * - 1008: Policy violation (auth failure)
 * - 1011: Internal server error
 */

/** Dev/QA only — options for `__capWsDebug.simulateUncleanDisconnect`. */
export type QaUncleanDisconnectOptions = {
  reason?: string;
  /**
   * Reject successful `connect()` for this long while the retry loop runs
   * (counter advances: 1/14, 2/14, …). Default 2500.
   */
  holdMs?: number;
  /**
   * Extra time to keep rejecting `connect()` after `holdMs`. Default 0.
   * Total block = `holdMs + blockReconnectMs`.
   */
  blockReconnectMs?: number;
};

export class WebSocketService {
  private ws: WebSocket | null = null;
  private isConnecting = false;
  private eventListeners: Map<string, Set<(event: NodeStatusEvent) => void>> =
    new Map();
  private authToken: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private maxReconnectDelay = 30000; // Max 30 seconds
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private shouldReconnect = true;
  private isActivelyReconnecting = false;
  private isWorkflowExecuting = false;
  private disconnectReason: string | null = null;
  private keepAliveInterval: NodeJS.Timeout | null = null;
  private readonly KEEPALIVE_INTERVAL_MS = 25000;
  private readonly WORKFLOW_MAX_RECONNECT_ATTEMPTS = 15;
  private readonly WORKFLOW_RECONNECT_DELAY = 500;
  private readonly WORKFLOW_MAX_RECONNECT_DELAY = 10000;
  private isFirstAttemptInProgress = false;
  private qaBlockReconnectUntil = 0;
  private qaUncleanDisconnectActive = false;
  private isBrowserOffline =
    typeof navigator !== 'undefined' ? !navigator.onLine : false;
  private onlineHandler: (() => void) | null = null;
  private offlineHandler: (() => void) | null = null;
  private url: string | null = null;

  constructor() {
    this.setupNetworkListeners();
  }

  private setupNetworkListeners(): void {
    this.offlineHandler = () => {
      this.isBrowserOffline = true;
      console.log('[WebSocket] Browser went offline', {
        isWorkflowExecuting: this.isWorkflowExecuting,
        isReconnecting: this.isActivelyReconnecting,
        timestamp: new Date().toISOString(),
      });

      // Pause reconnection — attempts will just fail while offline
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }

      // Force-close the WebSocket so the UI immediately reflects disconnection.
      // Don't set isActivelyReconnecting — the offline state is communicated
      // via isOffline() and the status bar renders the Offline bar directly.
      if (this.ws) {
        console.log('[WebSocket] Dropping connection due to network offline');
        this.stopKeepalive();
        this.isActivelyReconnecting = false;
        this.isFirstAttemptInProgress = false;
        try {
          this.ws.close();
        } catch {
          // Ignore close errors
        }
        this.ws = null;
      }
    };

    this.onlineHandler = () => {
      this.isBrowserOffline = false;
      console.log('[WebSocket] Browser came back online', {
        isWorkflowExecuting: this.isWorkflowExecuting,
        reconnectAttempts: this.reconnectAttempts,
        timestamp: new Date().toISOString(),
      });

      // Reconnect silently — treat as first attempt so UI stays calm
      if (this.shouldReconnect && !this.isConnected()) {
        console.log(
          '[WebSocket] Network restored, attempting silent reconnect',
        );
        this.reconnectAttempts = 0;
        this.isFirstAttemptInProgress = true;
        this.attemptReconnect();
      }
    };

    window.addEventListener('offline', this.offlineHandler);
    window.addEventListener('online', this.onlineHandler);
  }

  /**
   * Set the authentication token for WebSocket connections
   */
  setAuthToken(token: string | null): void {
    const tokenPreview = token ? `${token.substring(0, 10)}...` : 'null';
    console.log('[WebSocket] setAuthToken called:', {
      hadToken: !!this.authToken,
      hasNewToken: !!token,
      tokenPreview,
      isConnected: this.isConnected(),
      reconnectAttempts: this.reconnectAttempts,
      timestamp: new Date().toISOString(),
    });

    if (!token && this.isConnected()) {
      console.warn(
        '[WebSocket] Token set to null while connected - this may cause auth issues on reconnect',
      );
    }

    this.authToken = token;
  }

  /**
   * Set the authentication token for WebSocket connections (alias for setAuthToken)
   */
  onConnect(token: string | null): void {
    this.authToken = token;
  }

  /**
   * Get the authentication token
   */
  getAuthToken(): string | null {
    return this.authToken;
  }

  /**
   * Build WebSocket URL with authentication token as query parameter
   */
  private buildWebSocketUrl(baseUrl: string): string {
    if (!this.authToken) {
      console.warn('[WebSocket] buildWebSocketUrl: No auth token available!', {
        url: baseUrl,
        reconnectAttempts: this.reconnectAttempts,
        timestamp: new Date().toISOString(),
      });
      return baseUrl;
    }

    try {
      const url = new URL(baseUrl);
      url.searchParams.set('token', this.authToken);
      const tokenPreview = this.authToken.substring(0, 10);
      console.log('[WebSocket] buildWebSocketUrl: Token added to URL', {
        tokenPreview: `${tokenPreview}...`,
        urlWithoutToken: baseUrl,
        timestamp: new Date().toISOString(),
      });
      return url.toString();
    } catch (error) {
      console.error('[WebSocket] Error building WebSocket URL:', error);
      Sentry.captureException(error, {
        extra: {
          context: 'websocket-url-building',
          originalUrl: baseUrl,
          hasToken: !!this.authToken,
        },
      });
      return baseUrl;
    }
  }

  /**
   * Update the WebSocket URL and reconnect if currently connected
   */
  async updateUrl(newUrl: string): Promise<void> {
    console.log('[WebSocket] updateUrl called:', {
      oldUrl: this.url,
      newUrl,
      urlChanged: this.url !== newUrl,
      isConnected: this.isConnected(),
      hasToken: !!this.authToken,
      timestamp: new Date().toISOString(),
    });

    if (this.url !== newUrl) {
      this.url = newUrl;

      // If currently connected, disconnect and reconnect to new URL
      if (this.isConnected()) {
        console.log('[WebSocket] URL changed while connected, reconnecting:', {
          newUrl: this.getCurrentUrl(),
          readyState: this.ws?.readyState,
          hasToken: !!this.authToken,
          timestamp: new Date().toISOString(),
        });
        this.disconnect();
        // Auto-reconnect after a short delay
        await new Promise((resolve) => setTimeout(resolve, 100));
        try {
          await this.connect();
          console.log('[WebSocket] Successfully reconnected to new URL');
        } catch (error) {
          console.error(
            '[WebSocket] Failed to reconnect after URL update:',
            error,
          );
          Sentry.captureException(error, {
            level: 'warning',
            extra: {
              context: 'websocket-reconnect-after-url-update',
              newUrl: newUrl,
              hasToken: !!this.authToken,
            },
          });
          throw error;
        }
      } else {
        console.log(
          '[WebSocket] URL updated but not connected, no action needed',
        );
      }
    } else {
      console.log('[WebSocket] URL unchanged, no action needed');
    }
  }

  private startKeepalive(): void {
    this.stopKeepalive();
    this.keepAliveInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({ type: 'ping' }));
        } catch {
          // Send failed; onclose will fire and trigger reconnection
        }
      }
    }, this.KEEPALIVE_INTERVAL_MS);
  }

  private stopKeepalive(): void {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }
  }

  private get effectiveMaxReconnectAttempts(): number {
    return this.isWorkflowExecuting
      ? this.WORKFLOW_MAX_RECONNECT_ATTEMPTS
      : this.maxReconnectAttempts;
  }

  private get effectiveReconnectDelay(): number {
    return this.isWorkflowExecuting
      ? this.WORKFLOW_RECONNECT_DELAY
      : this.reconnectDelay;
  }

  private get effectiveMaxReconnectDelay(): number {
    return this.isWorkflowExecuting
      ? this.WORKFLOW_MAX_RECONNECT_DELAY
      : this.maxReconnectDelay;
  }

  /**
   * Attempt to reconnect with exponential backoff.
   * Pauses while browser is offline — the online handler will resume.
   */
  private async attemptReconnect(): Promise<void> {
    if (this.isBrowserOffline) {
      console.log(
        '[WebSocket] Skipping reconnect attempt — browser is offline. Will retry on network restore.',
      );
      this.isActivelyReconnecting = true;
      this.isFirstAttemptInProgress = false;
      return;
    }

    if (
      !this.shouldReconnect ||
      !this.url ||
      this.reconnectAttempts >= this.effectiveMaxReconnectAttempts
    ) {
      this.isActivelyReconnecting = false;
      this.isFirstAttemptInProgress = false;
      console.warn('[WebSocket] Reconnection stopped:', {
        shouldReconnect: this.shouldReconnect,
        attempts: this.reconnectAttempts,
        maxAttempts: this.effectiveMaxReconnectAttempts,
        isWorkflowExecuting: this.isWorkflowExecuting,
        hasToken: !!this.authToken,
        reason: !this.shouldReconnect
          ? 'Reconnection disabled'
          : !this.url
            ? 'No session URL set'
            : 'Max attempts reached',
      });

      if (this.reconnectAttempts >= this.effectiveMaxReconnectAttempts) {
        console.error(
          '[WebSocket] CRITICAL: All reconnection attempts exhausted!',
          {
            attempts: this.reconnectAttempts,
            maxAttempts: this.effectiveMaxReconnectAttempts,
            isWorkflowExecuting: this.isWorkflowExecuting,
            hasToken: !!this.authToken,
            url: this.getCurrentUrl(),
          },
        );

        Sentry.captureException(
          new Error('WebSocket reconnection failed after max attempts'),
          {
            level: 'warning',
            extra: {
              context: 'websocket-reconnection-exhausted',
              attempts: this.reconnectAttempts,
              maxAttempts: this.effectiveMaxReconnectAttempts,
              isWorkflowExecuting: this.isWorkflowExecuting,
              hasToken: !!this.authToken,
            },
          },
        );
      }

      return;
    }

    this.reconnectAttempts++;

    const qaSimulating =
      isCapWsDebugEnvironment() &&
      this.qaUncleanDisconnectActive &&
      this.qaBlockReconnectUntil > Date.now();

    // First attempt is silent — don't expose isReconnecting to UI (except QA sim)
    if (this.reconnectAttempts === 1 && !qaSimulating) {
      this.isFirstAttemptInProgress = true;
      this.isActivelyReconnecting = false;
    } else {
      this.isFirstAttemptInProgress = false;
      this.isActivelyReconnecting = true;
    }

    const delay = Math.min(
      this.effectiveReconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.effectiveMaxReconnectDelay,
    );

    console.log('[WebSocket] Scheduling reconnection attempt:', {
      attempt: this.reconnectAttempts,
      maxAttempts: this.effectiveMaxReconnectAttempts,
      delay: `${delay}ms`,
      silent: this.isFirstAttemptInProgress,
      isWorkflowExecuting: this.isWorkflowExecuting,
      hasToken: !!this.authToken,
      url: this.getCurrentUrl(),
      timestamp: new Date().toISOString(),
    });

    this.reconnectTimeout = setTimeout(async () => {
      console.log('[WebSocket] Executing reconnection attempt now:', {
        attempt: this.reconnectAttempts,
        hasToken: !!this.authToken,
        timestamp: new Date().toISOString(),
      });

      try {
        await this.connect();
        console.log('[WebSocket] Reconnection successful!', {
          attempt: this.reconnectAttempts,
          isWorkflowExecuting: this.isWorkflowExecuting,
          hasToken: !!this.authToken,
          timestamp: new Date().toISOString(),
        });
        this.isActivelyReconnecting = false;
        this.isFirstAttemptInProgress = false;
        this.reconnectAttempts = 0;
      } catch (error) {
        console.error('[WebSocket] Reconnection attempt failed:', {
          attempt: this.reconnectAttempts,
          error: error instanceof Error ? error.message : String(error),
          isWorkflowExecuting: this.isWorkflowExecuting,
          hasToken: !!this.authToken,
          timestamp: new Date().toISOString(),
        });

        // First attempt failed — subsequent attempts are visible
        this.isFirstAttemptInProgress = false;
        this.isActivelyReconnecting = true;

        await this.attemptReconnect();
      }
    }, delay);
  }

  /**
   * Enable or disable automatic reconnection
   */
  setReconnectEnabled(enabled: boolean): void {
    this.shouldReconnect = enabled;
    if (!enabled) {
      this.isActivelyReconnecting = false;
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }
    }
  }

  async connect(): Promise<void> {
    if (!this.url) {
      console.error(
        '[WebSocket] Refusing to connect: no session URL has been set',
        {
          reconnectAttempts: this.reconnectAttempts,
          timestamp: new Date().toISOString(),
        },
      );
      Sentry.captureException(
        new Error('WebSocket connect attempted before a session URL was set'),
        {
          level: 'error',
          extra: {
            context: 'websocket-connect-no-url',
            reconnectAttempts: this.reconnectAttempts,
          },
        },
      );
      throw new Error('WebSocket URL has not been set');
    }

    // Ensure we have a fresh token before connecting (handles Auth0 refresh)
    const freshToken = await getFreshToken();
    if (freshToken) {
      this.authToken = freshToken;
    }

    console.log('WebSocket: connect() called', {
      hasAuthToken: !!this.authToken,
      currentReadyState: this.ws?.readyState,
      isConnecting: this.isConnecting,
      reconnectAttempts: this.reconnectAttempts,
      browserOnline: navigator.onLine,
      timestamp: new Date().toISOString(),
    });

    if (isCapWsDebugEnvironment() && this.qaBlockReconnectUntil > Date.now()) {
      const remainingMs = this.qaBlockReconnectUntil - Date.now();
      console.log('[WebSocket] QA reconnect blocked', {
        remainingMs,
        until: new Date(this.qaBlockReconnectUntil).toISOString(),
        timestamp: new Date().toISOString(),
      });
      throw new Error('QA reconnect blocked');
    }

    return new Promise((resolve, reject) => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        console.log('[WebSocket] Already connected, resolving immediately');
        resolve();
        return;
      }

      if (this.isConnecting) {
        console.warn('[WebSocket] Connection already in progress, rejecting');
        reject(new Error('Connection already in progress'));
        return;
      }

      if (!this.authToken) {
        console.error(
          '[WebSocket] CRITICAL: Attempting to connect without auth token!',
          {
            reconnectAttempts: this.reconnectAttempts,
            url: this.url,
            timestamp: new Date().toISOString(),
          },
        );
        Sentry.captureException(
          new Error('WebSocket connection attempted without auth token'),
          {
            level: 'error',
            extra: {
              context: 'websocket-connect-no-token',
              reconnectAttempts: this.reconnectAttempts,
              url: this.url,
            },
          },
        );
      }

      this.isConnecting = true;

      // Clear any existing reconnection timeout
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }

      try {
        // Read the URL here rather than before the token refresh above:
        // updateUrl may have switched sessions while that was in flight.
        const sessionUrl = this.url;
        if (!sessionUrl) {
          throw new Error('WebSocket URL has not been set');
        }

        // Add token as query parameter if available
        const url = this.buildWebSocketUrl(sessionUrl);

        console.log('[WebSocket] Creating new WebSocket connection...', {
          hasToken: !!this.authToken,
          reconnectAttempts: this.reconnectAttempts,
          timestamp: new Date().toISOString(),
        });

        this.ws = new WebSocket(url);
        this.ws.onopen = () => {
          console.log('WebSocket connected to node status service:', {
            url: this.getCurrentUrl(),
            readyState: this.ws?.readyState,
            timestamp: new Date().toISOString(),
          });
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.qaBlockReconnectUntil = 0;
          this.qaUncleanDisconnectActive = false;
          this.startKeepalive();
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            // Check if the message is empty or just whitespace
            if (!event.data || event.data.trim() === '') {
              console.warn('Received empty WebSocket message');
              return;
            }

            // Preprocess the message to handle single quotes (Python-style JSON)
            let processedData = event.data;

            // Only convert if the message starts with single quote (Python dict style)
            // Valid JSON messages may contain single quotes in their content
            if (processedData.trim().startsWith("'")) {
              // More robust conversion that handles nested quotes properly
              processedData = convertPythonDictToJSON(processedData);
            }

            const data: NodeStatusEvent = JSON.parse(processedData);
            // Normalize server timestamp: ISO strings without offset get Z (UTC); numbers
            // must be Unix s/ms or we fall back (e.g. ping events with monotonic floats).
            data.timestamp = normalizeServerTimestamp(data.timestamp);
            this.handleMessage(data);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
            console.error('Raw message that failed to parse:', event.data);

            Sentry.captureException(error, {
              level: 'error',
              extra: {
                context: 'websocket-message-parse-error',
                rawMessage: event.data,
                messageLength: event.data?.length,
              },
            });
          }
        };

        this.ws.onclose = (event) => {
          this.stopKeepalive();

          const closeInfo = {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
            url: this.getCurrentUrl(),
            isWorkflowExecuting: this.isWorkflowExecuting,
            shouldReconnect: this.shouldReconnect,
            reconnectAttempts: this.reconnectAttempts,
            maxAttempts: this.effectiveMaxReconnectAttempts,
            disconnectReason: this.disconnectReason,
            hasToken: !!this.authToken,
            browserOnline: navigator.onLine,
            timestamp: new Date().toISOString(),
          };

          // Log with appropriate level based on close code
          if (event.code === 1008) {
            console.error(
              '[WebSocket] Connection closed: POLICY VIOLATION (likely auth failure)',
              closeInfo,
            );
          } else if (event.code === 1011) {
            console.error(
              '[WebSocket] Connection closed: INTERNAL SERVER ERROR',
              closeInfo,
            );
          } else if (!event.wasClean) {
            console.warn(
              '[WebSocket] Connection closed unexpectedly (not clean)',
              closeInfo,
            );
          } else {
            console.log('[WebSocket] Connection closed normally', closeInfo);
          }

          // Track all unclean disconnections during workflow execution in Sentry
          if (!event.wasClean && this.isWorkflowExecuting) {
            Sentry.captureMessage(
              'WebSocket disconnected during workflow execution',
              {
                level: 'warning',
                tags: {
                  component: 'WebSocketService',
                  closeCode: String(event.code),
                },
                extra: {
                  code: event.code,
                  reason: event.reason,
                  reconnectAttempt: this.reconnectAttempts,
                  browserOnline: navigator.onLine,
                  hasToken: !!this.authToken,
                },
              },
            );
          }

          // Additional warning if closed without token
          if (!this.authToken && event.code !== 1000) {
            console.warn(
              '[WebSocket] Connection closed and no token available - may fail to reconnect',
            );
          }

          this.isConnecting = false;

          // Reconnect on unexpected disconnections OR server errors (1011)
          // even if wasClean is true (browsers mark 1011 as clean)
          const shouldAttemptReconnect =
            (!event.wasClean || event.code === 1011) &&
            this.shouldReconnect &&
            this.reconnectAttempts < this.effectiveMaxReconnectAttempts;

          if (shouldAttemptReconnect) {
            console.log(
              'WebSocket: Unexpected disconnection detected, attempting to reconnect...',
              {
                wasClean: event.wasClean,
                code: event.code,
                isWorkflowExecuting: this.isWorkflowExecuting,
                reconnectAttempts: this.reconnectAttempts,
                maxAttempts: this.effectiveMaxReconnectAttempts,
                reason: event.reason,
              },
            );
            this.attemptReconnect();
          } else if (
            event.wasClean &&
            event.code !== 1011 &&
            !(isCapWsDebugEnvironment() && this.qaUncleanDisconnectActive)
          ) {
            console.log(
              'WebSocket: Clean disconnection, resetting reconnect attempts',
            );
            this.reconnectAttempts = 0;
          } else if (
            this.reconnectAttempts >= this.effectiveMaxReconnectAttempts
          ) {
            console.warn(
              'WebSocket: Max reconnection attempts reached, giving up reconnection',
              {
                attempts: this.reconnectAttempts,
                maxAttempts: this.effectiveMaxReconnectAttempts,
                isWorkflowExecuting: this.isWorkflowExecuting,
              },
            );
          }
        };

        this.ws.onerror = (event) => {
          const readyState = this.ws?.readyState;
          const readyStateLabel =
            readyState === WebSocket.CONNECTING
              ? 'CONNECTING'
              : readyState === WebSocket.OPEN
                ? 'OPEN'
                : readyState === WebSocket.CLOSING
                  ? 'CLOSING'
                  : readyState === WebSocket.CLOSED
                    ? 'CLOSED'
                    : 'UNKNOWN';

          const debugInfo = {
            url: this.getCurrentUrl(),
            readyState,
            readyStateLabel,
            reconnectAttempts: this.reconnectAttempts,
            maxReconnectAttempts: this.maxReconnectAttempts,
            isWorkflowExecuting: this.isWorkflowExecuting,
            shouldReconnect: this.shouldReconnect,
            hasAuthToken: !!this.authToken,
            browserOnline: navigator.onLine,
            timestamp: new Date().toISOString(),
          };

          console.error('[WebSocket] WebSocket error event:', debugInfo, event);

          // WebSocket onerror receives a generic Event, not an Error.
          // Create a proper Error so Sentry can capture a meaningful message.
          const wsError = new Error(
            `WebSocket connection error (state=${readyStateLabel}, online=${navigator.onLine}, hasToken=${!!this.authToken}): ${this.getCurrentUrl()}`,
          );

          Sentry.captureException(wsError, {
            level: 'error',
            extra: {
              context: 'websocket-connection-error',
              ...debugInfo,
              errorType: event?.type,
            },
          });

          this.isConnecting = false;
          reject(wsError);
        };
      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  private handleMessage(event: NodeStatusEvent): void {
    const eventType = event.event_type as string | undefined;

    // Drop server-side keepalive pings before any listener sees
    // them.  The BE's ``monitor_session_and_send_events``
    // (api/websockets/redis_manager.py) emits
    // ``{event_type: "ping", timestamp: ...}`` every 30s on every
    // workflow WebSocket purely to keep the TCP connection warm
    // through Docker vpnkit / OS idle timers / browser tab
    // throttling.  They have NO application meaning and were
    // accidentally surfacing in the events panel as "system
    // events", which is just noise to the user.  Filtering at
    // this single chokepoint guarantees no current OR future
    // subscriber has to remember to handle them.
    if (eventType === 'ping') {
      return;
    }

    // Lightweight diagnostic for the run-level events: lifecycle
    // (pause/resume/edit/rerun_seeded), termination, execution
    // result.  These are rare (handful per run) so the noise is
    // acceptable and being able to grep for [WebSocket:Event] in
    // a user's console during a bug report is invaluable.  Per-node
    // events are skipped to avoid console flooding during streams.
    const isRunLevel =
      eventType === 'lifecycle' ||
      eventType === 'termination' ||
      eventType === 'execution_result' ||
      eventType === 'status_update';
    if (isRunLevel) {
      const phase =
        eventType === 'lifecycle'
          ? (event as unknown as { phase?: string }).phase
          : undefined;
      console.log(
        '[WebSocket:Event]',
        eventType,
        phase ? `phase=${phase}` : '',
      );
    }

    // Emit to all listeners for this event type
    const listeners = this.eventListeners.get(event.event_type);
    if (listeners) {
      listeners.forEach((listener) => listener(event));
    }

    // Also emit to general listeners
    const generalListeners = this.eventListeners.get('*');
    if (generalListeners) {
      generalListeners.forEach((listener) => listener(event));
    }
  }

  subscribe(
    eventType: string,
    callback: (event: NodeStatusEvent) => void,
  ): () => void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, new Set());
    }

    const listeners = this.eventListeners.get(eventType)!;
    listeners.add(callback);

    // Return unsubscribe function
    return () => {
      listeners.delete(callback);
      if (listeners.size === 0) {
        this.eventListeners.delete(eventType);
      }
    };
  }

  disconnect(reason: string = 'Manual disconnect'): void {
    this.disconnectReason = reason;

    console.log('WebSocket disconnect initiated:', {
      reason,
      url: this.getCurrentUrl(),
      readyState: this.ws?.readyState,
      isWorkflowExecuting: this.isWorkflowExecuting,
      reconnectAttempts: this.reconnectAttempts,
      shouldReconnect: this.shouldReconnect,
      timestamp: new Date().toISOString(),
    });

    if (this.isWorkflowExecuting) {
      console.warn(
        'WebSocket: Disconnecting while workflow is executing - this may interrupt message flow!',
        {
          reason,
          workflowExecuting: this.isWorkflowExecuting,
          timestamp: new Date().toISOString(),
        },
      );
    }

    this.shouldReconnect = false;
    this.qaBlockReconnectUntil = 0;
    this.qaUncleanDisconnectActive = false;
    this.stopKeepalive();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      console.log('Manually disconnecting WebSocket:', {
        url: this.getCurrentUrl(),
        readyState: this.ws.readyState,
        reason,
        timestamp: new Date().toISOString(),
      });
      this.ws.close();
      this.ws = null;
    }
    this.isConnecting = false;
    this.reconnectAttempts = 0;
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  getCurrentUrl(): string {
    return this.url?.replace(/token=[^&]*/, 'token=*****') ?? '';
  }

  /**
   * Get reconnection status.
   * The first reconnection attempt is silent (isReconnecting stays false)
   * so the UI remains in RUNNING state during the initial fast retry.
   */
  getReconnectStatus(): {
    isReconnecting: boolean;
    attempts: number;
    maxAttempts: number;
    enabled: boolean;
  } {
    return {
      isReconnecting:
        this.isActivelyReconnecting && !this.isFirstAttemptInProgress,
      attempts: this.reconnectAttempts,
      maxAttempts: this.effectiveMaxReconnectAttempts,
      enabled: this.shouldReconnect,
    };
  }

  /**
   * Whether the browser is currently offline (no network).
   */
  isOffline(): boolean {
    return this.isBrowserOffline;
  }

  /**
   * Enable reconnection (useful when starting a new workflow)
   */
  enableReconnection(): void {
    this.shouldReconnect = true;
    this.reconnectAttempts = 0;
  }

  /**
   * Mark workflow as started to prevent premature disconnection
   */
  markWorkflowStarted(): void {
    console.log('WebSocket: Marking workflow as started');
    this.isWorkflowExecuting = true;
    this.shouldReconnect = true;
    this.isActivelyReconnecting = false;
    this.reconnectAttempts = 0;
  }

  /**
   * Mark workflow as completed to allow disconnection
   */
  markWorkflowCompleted(): void {
    console.log('WebSocket: Marking workflow as completed');
    this.isWorkflowExecuting = false;
  }

  /**
   * Check if workflow is currently executing
   */
  isWorkflowRunning(): boolean {
    return this.isWorkflowExecuting;
  }

  /**
   * Remove global event listeners. Call during HMR teardown or tests
   * to prevent duplicate handler accumulation.
   */
  dispose(): void {
    if (this.offlineHandler) {
      window.removeEventListener('offline', this.offlineHandler);
    }
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
    }
    this.stopKeepalive();
  }

  /**
   * Used by `registerCapWsDebug` only — abnormal close with reconnection enabled.
   * Unlike {@link disconnect}, does not set `shouldReconnect = false`.
   */
  simulateUncleanDisconnectForQA(
    options: QaUncleanDisconnectOptions | string = {},
  ): void {
    if (!isCapWsDebugEnvironment()) {
      return;
    }

    const opts = typeof options === 'string' ? { reason: options } : options;
    const reason = opts.reason ?? 'QA unclean disconnect';
    const holdMs = opts.holdMs ?? 2500;
    const blockReconnectMs = opts.blockReconnectMs ?? 0;

    console.log('[WebSocket] QA unclean disconnect:', {
      reason,
      holdMs,
      blockReconnectMs,
      url: this.getCurrentUrl(),
      readyState: this.ws?.readyState,
      isWorkflowExecuting: this.isWorkflowExecuting,
      timestamp: new Date().toISOString(),
    });

    const totalBlockMs = holdMs + blockReconnectMs;
    this.qaUncleanDisconnectActive = true;
    this.qaBlockReconnectUntil =
      totalBlockMs > 0 ? Date.now() + totalBlockMs : 0;
    this.shouldReconnect = true;
    this.isActivelyReconnecting = true;
    this.isFirstAttemptInProgress = false;
    this.stopKeepalive();

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    const socket = this.ws;
    this.ws = null;
    this.isConnecting = false;

    if (socket && socket.readyState !== WebSocket.CLOSED) {
      // Scripted close() only allows 1000 or 3000–4999 (not 1006/1011).
      socket.close(4000, reason);
    }

    // Browser-initiated 4xxx closes are often `wasClean`, so onclose may skip
    // attemptReconnect — start the retry loop immediately while connect() is
    // blocked so the Reconnecting counter advances during holdMs.
    queueMicrotask(() => {
      if (
        this.shouldReconnect &&
        !this.isConnected() &&
        this.reconnectAttempts < this.effectiveMaxReconnectAttempts
      ) {
        this.attemptReconnect();
      }
    });
  }
}

// Create a singleton instance
export const websocketService = new WebSocketService();

if (isCapWsDebugEnvironment()) {
  registerCapWsDebug(websocketService);
}
